import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Auth, Cache } from 'aws-amplify';
import { persistCache } from 'apollo-cache-persist';
import localForage from 'localforage';
import {
	ApolloProvider,
	ApolloClient,
	InMemoryCache,
	HttpLink,
	ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import axios from 'axios';
import get from 'lodash/get';
import {
	downloadFromS3Signed,
	parseJwt,
	graphql,
} from 'src/_shared/services/utils.js';
import { awsApiKey } from './awsExports.js';
import DefaultSpinner from './DefaultSpinnerComponent.jsx';
import { decryptUsingAES256 } from './settings.js';
// Set ForceProdEnv true to force prod backend in development. //
import { configMode } from './configMode.js';

let { host } = window.location;
host = host.replace('www.', '');

if (
	host == 'heartlanddentalcareers.com' ||
	host == 'heartlandaffiliationreferrals.com'
) {
	window.location.href = `https://heartlanddentalreferrals.com/login`;
}

const awsMeta = awsApiKey;

export const refreshJwtToken = async (
	authType,
	isBackgroundRefresh = false
) => {
	try {
		if (authType === 'default') {
			const session = await Auth.currentSession();
			const token = session.getAccessToken().getJwtToken();
			Cookies.set('jwt', token);
			return token;
		}

		try {
			const federatedToken = Cache.getItem('federatedToken');
			if (!federatedToken || !federatedToken.refresh_token) {
				console.error('No federated token or refresh token found');
				throw new Error('No refresh token available');
			}

			const refreshToken = federatedToken.refresh_token;
			const domain = Cache.getItem('federatedDomain');
			const region = Cache.getItem('federatedRegion');
			const clientId = Cache.getItem('federatedClientId');
			const clientSecret = Cache.getItem('federatedClientSecret');

			if (!domain || !region || !clientId || !clientSecret) {
				console.error('Missing required federation parameters');
				throw new Error('Missing federation parameters');
			}

			const scope = 'aws.cognito.signin.user.admin+email+openid+phone+profile';
			const body = `client_id=${clientId}&client_secret=${clientSecret}&grant_type=refresh_token&refresh_token=${refreshToken}&scope=${scope}`;
			const url = `https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`;

			const response = await axios.post(url, body, {
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					Authorization: 'Basic ' + window.btoa(clientId + ':' + clientSecret),
				},
			});

			const tokens = response.data;
			// Preserve the refresh token as it might not be included in the response
			tokens.refresh_token ||= refreshToken;
			Cache.setItem('federatedToken', tokens);

			const token = tokens.id_token;
			Cookies.set('jwt', token);

			const expires_at = Date.now() + tokens.expires_in * 1000;
			const federatedRefreshToken = {
				token,
				expires_at,
				refresh_token: tokens.refresh_token,
			};
			Cache.setItem('federatedRefreshToken', federatedRefreshToken);

			// Reset failure count on successful refresh
			window.tokenRefreshFailCount = 0;

			return federatedRefreshToken;
		} catch (error) {
			console.error('Error refreshing federated token:', error);
			throw error;
		}
	} catch (error) {
		console.error('Session expired or token refresh failed:', error);
		// Don't redirect immediately if this is a background refresh
		if (!isBackgroundRefresh) {
			window.location.href = `${window.location.origin}/logout`;
		}

		throw error;
	}
};

const getJwtToken = async (authType) => {
	let jwtToken = null;
	if (authType === 'default') {
		// Username and password login
		jwtToken = async () => {
			return (await Auth.currentSession()).getAccessToken().getJwtToken();
		};
	} else if (authType === 'saml') {
		// Saml login
		jwtToken = async () => {
			try {
				let token;
				let shouldRefresh = false;

				// First try to get token from federatedRefreshToken cache
				const federatedRefreshToken = Cache.getItem('federatedRefreshToken');
				if (federatedRefreshToken && federatedRefreshToken.token) {
					token = federatedRefreshToken.token;

					// Check if this cached token is expired or about to expire
					const currentTime = Date.now();
					const expiresAt = federatedRefreshToken.expires_at;
					const FIVE_MINUTES = 5 * 60 * 1000;

					if (expiresAt - currentTime < FIVE_MINUTES) {
						shouldRefresh = true;
					}
				} else {
					// Fallback to federatedToken if no cached refresh token
					const federatedToken = Cache.getItem('federatedToken');
					if (federatedToken && federatedToken.id_token) {
						token = federatedToken.id_token;
						shouldRefresh = true; // Assume we need to refresh if no cached token
					} else {
						// Last resort: try Auth.currentAuthenticatedUser
						const user = await Auth.currentAuthenticatedUser();
						token = user.token;
						shouldRefresh = true;
					}
				}

				// If we need to refresh the token
				if (shouldRefresh) {
					try {
						const refreshedToken = await refreshJwtToken(authType, false);
						return refreshedToken.token;
					} catch (error) {
						console.error('Failed to refresh token:', error);
						// Continue with existing token if refresh fails
						return token;
					}
				}

				return token;
			} catch (error) {
				console.error('Error in SAML token retrieval:', error);
				throw error;
			}
		};
	}

	return jwtToken;
};

export const startTokenRefreshTimer = (authType) => {
	const REFRESH_INTERVAL = 50 * 60 * 1000;

	if (window.tokenRefreshTimer) {
		clearInterval(window.tokenRefreshTimer);
	}

	// Reset failure count when starting new timer
	window.tokenRefreshFailCount = 0;

	const performRefresh = async (isBackground = false) => {
		if (!Cookies.get('jwt') && !Cache.getItem('federatedToken')) {
			return;
		}

		try {
			await refreshJwtToken(authType, isBackground);
		} catch (error) {
			console.error('Error refreshing token:', error);
			if (isBackground) {
				const failCount = (window.tokenRefreshFailCount || 0) + 1;
				window.tokenRefreshFailCount = failCount;

				if (failCount >= 3) {
					console.error('Multiple token refresh failures, logging out');
					window.location.href = `${window.location.origin}/logout`;
				}
			}
		}
	};

	window.tokenRefreshTimer = setInterval(
		() => performRefresh(true),
		REFRESH_INTERVAL
	);

	const handleVisibilityChange = async () => {
		if (!document.hidden && authType === 'saml') {
			// User returned to the tab, check if token needs refresh
			try {
				const federatedRefreshToken = Cache.getItem('federatedRefreshToken');
				if (federatedRefreshToken) {
					const currentTime = Date.now();
					const expiresAt = federatedRefreshToken.expires_at;

					// If token expires within 10 minutes, refresh it
					const TEN_MINUTES = 10 * 60 * 1000;
					if (expiresAt - currentTime < TEN_MINUTES)
						await performRefresh(false);
				}
			} catch (error) {
				console.error('Error during visibility change token check:', error);
			}
		}
	};

	// Remove existing listener if any
	if (window.visibilityChangeListener) {
		document.removeEventListener(
			'visibilitychange',
			window.visibilityChangeListener
		);
	}

	// Add new listener
	window.visibilityChangeListener = handleVisibilityChange;
	document.addEventListener('visibilitychange', handleVisibilityChange);

	// For SAML, check token validity on page load and refresh if needed
	if (authType === 'saml' && Cache.getItem('federatedToken')) {
		setTimeout(async () => {
			try {
				const federatedRefreshToken = Cache.getItem('federatedRefreshToken');
				if (federatedRefreshToken) {
					const currentTime = Date.now();
					const expiresAt = federatedRefreshToken.expires_at;

					// If token expires within 10 minutes, refresh it
					const TEN_MINUTES = 10 * 60 * 1000;
					if (expiresAt - currentTime < TEN_MINUTES)
						await performRefresh(false);
				}
			} catch (error) {
				console.error('Error during initial token check:', error);
			}
		}, 1000);
	}
};

const createClient = async (authType) => {
	const { url } = awsMeta;
	const authLink = setContext(async (_, { headers }) => {
		try {
			const jwtTokenFn = await getJwtToken(authType);
			const token = await jwtTokenFn();

			return {
				headers: {
					...headers,
					Authorization: token,
				},
			};
		} catch (error) {
			console.error('Error getting auth token:', error);
			return { headers };
		}
	});

	const httpLink = new HttpLink({
		uri: url,
	});

	const link = authLink.concat(httpLink);

	const cache = new InMemoryCache({});
	await persistCache({
		cache,
		storage: localForage,
	});
	return new ApolloClient({
		link,
		cache,
		assumeImmutableResults: false,
	});
};

export function WithApiProvider({ Component }) {
	const [client, setClient] = useState(null);
	const [saml, setSaml] = useState(undefined);
	const [company, setCompany] = useState(null);
	const [key, setKey] = useState(0);

	useEffect(() => {
		let isMounted = true;

		const initializeClient = async () => {
			let authType = localStorage.getItem('authType');
			if (!authType) {
				authType = 'default';
				localStorage.setItem('authType', authType);
			}

			try {
				awsMeta.url = decryptUsingAES256(
					process.env[`REACT_APP_${configMode}_APPSYNCENDPOINT`]
				);
				const newClient = await createClient(authType);
				// Only update state if component is still mounted
				if (isMounted) {
					setClient(newClient);
					startTokenRefreshTimer(authType);
				}

				let companyData;
				try {
					companyData = await graphql({
						input: { host },
						query: 'getCompanyByHost',
					});
					if (isMounted) {
						setCompany(companyData);
					}
				} catch (error) {
					console.error(error);
					return;
				}

				const favicon = document.querySelector('#favicon');
				const favTitle = document.querySelector('#faviconTitle');
				switch (host) {
					case 'app.erinapp.com': {
						favTitle.innerHTML = 'ERIN | Employee Referral Platform';
						favicon.setAttribute('href', '/erin_square.png');

						break;
					}

					case 'referrals.aus.com':
					case 'referralstest.aus.com': {
						favTitle.innerHTML = 'Allied Referrals';
						favicon.setAttribute('href', '/alliedfavicon.ico');

						break;
					}

					case 'referrals.pierpoint.com': {
						favTitle.innerHTML = 'Pierpoint | Referral Portal';
						favicon.setAttribute('href', '/Pierpoint.png');

						break;
					}

					case 'app.refervets.org': {
						favTitle.innerHTML = 'Refervets | Veteran Referral Network';
						favicon.setAttribute('href', '/refervets.png');

						break;
					}

					case 'referafriend.seaworldentertainment.com': {
						favTitle.innerHTML = 'Join Me At SEA';
						favicon.setAttribute('href', '/seaworld-favicon.ico');

						break;
					}

					case 'qa.referafriend.seaworldentertainment.com': {
						favTitle.innerHTML = 'Join Me At SEA';
						favicon.setAttribute('href', '/seaworld-favicon.ico');

						break;
					}

					case 'connect.myhatchpad.com': {
						favTitle.innerHTML = 'Hatchpad Connect';
						favicon.setAttribute('href', '/hatchpad.ico');

						break;
					}

					case 'referrals.sunriseseniorliving.com': {
						favTitle.innerHTML = 'Sunrise Employee Referrals';
						favicon.setAttribute('href', '/sunrise-favicon.ico');

						break;
					}

					case 'pinteresttalenthub.com': {
						favTitle.innerHTML =
							'Pinterest Talent Hub | Employee Referral Portal';
						favicon.setAttribute('href', '/pinterest_favicon.png');

						break;
					}

					case 'referrals.primaryaimllc.com': {
						favTitle.innerHTML = 'Primary Aim Referrals';
						favicon.setAttribute('href', '/primary_aim.jpeg');

						break;
					}

					default: {
						favTitle.innerHTML = companyData?.faviconTitle
							? companyData?.faviconTitle
							: 'ERIN | Employee Referral Portal';
						if (companyData?.favicon?.key) {
							const s3url = await downloadFromS3Signed(
								companyData.favicon.key,
								companyData.favicon.bucket
							);
							favicon.setAttribute('href', s3url);
						} else {
							favicon.setAttribute('href', '/erin_square.png');
						}
					}
				}

				try {
					const samlData = await graphql({
						input: { companyId: companyData?.id },
						query: 'getSAMLAuthByCompanyId',
					});
					if (isMounted) {
						setSaml(samlData);
					}
				} catch (error) {
					console.error('Error fetching SAML data:', error);
				}
			} catch (error) {
				console.log(error);
			}
		};

		initializeClient();

		return () => {
			isMounted = false;
		};
	}, [key]);

	const handleSignOut = async () => {
		try {
			// Clean up timers and listeners
			if (window.tokenRefreshTimer) {
				clearInterval(window.tokenRefreshTimer);
				window.tokenRefreshTimer = null;
			}

			if (window.visibilityChangeListener) {
				document.removeEventListener(
					'visibilitychange',
					window.visibilityChangeListener
				);
				window.visibilityChangeListener = null;
			}

			window.tokenRefreshFailCount = 0;

			await Auth.signOut();
			localStorage.clear();
			localForage.clear();
			Cache.clear();
			Cookies.remove('jwt');
			client.clearStore();

			setKey((prevKey) => prevKey + 1); // To force reinit of apollo client
		} catch (error) {
			console.log('Error signing out', error);
		}
	};

	if (!client || saml === undefined) {
		return (
			<div
				style={{ height: '100%', width: '100%', backgroundColor: '#1b365c' }}
			>
				<DefaultSpinner />
			</div>
		);
	}

	return (
		<ApolloProvider client={client}>
			<Component
				secrets={{
					GoogleAuthClientID:
						process.env[`REACT_APP_${configMode}_GOOGLEAUTHCLIENTID`],
				}}
				handleSignOut={handleSignOut}
			/>
		</ApolloProvider>
	);
}
